import {
  computed,
  onMounted,
  onBeforeUnmount,
  ref,
  getCurrentInstance,
} from 'vue';
import { downloadFile } from '@chatwoot/utils';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

export const useAudioPlayback = (audioPlayer, attachment) => {
  const isPlaying = ref(false);
  const isMuted = ref(false);
  const currentTime = ref(0);
  const duration = ref(0);
  const playbackSpeed = ref(1);
  const playbackError = ref(false);
  const { uid } = getCurrentInstance();
  let disposed = false;
  let pendingPlay = false;
  let removeDurationListener;
  const playbackSpeedLabel = computed(() => `${playbackSpeed.value}x`);

  const onLoadedMetadata = event => {
    if (disposed) return;
    const el = event?.currentTarget || audioPlayer.value;
    if (Number.isFinite(el.duration)) {
      duration.value = el.duration;
    } else if (el.duration === Infinity) {
      // WebM recordings may omit the Duration header; seeking forces a scan.
      removeDurationListener?.();
      const resolveDuration = () => {
        removeDurationListener();
        el.currentTime = 0;
        if (Number.isFinite(el.duration)) duration.value = el.duration;
      };
      removeDurationListener = () =>
        el.removeEventListener('timeupdate', resolveDuration);
      el.addEventListener('timeupdate', resolveDuration);
      try {
        el.currentTime = Number.MAX_SAFE_INTEGER;
      } catch {
        removeDurationListener();
      }
    }
  };

  onMounted(() => {
    onLoadedMetadata();
    audioPlayer.value.playbackRate = playbackSpeed.value;
  });
  useEmitter('pause_playing_audio', currentPlayingId => {
    if (currentPlayingId !== uid) audioPlayer.value?.pause();
  });
  onBeforeUnmount(() => {
    disposed = true;
    removeDurationListener?.();
    audioPlayer.value?.pause();
  });

  const onPlay = () => {
    if (disposed) return;
    isPlaying.value = true;
    emitter.emit('pause_playing_audio', uid);
  };
  const onPause = () => {
    isPlaying.value = false;
  };
  const onError = () => {
    playbackError.value = true;
  };
  const playOrPause = async () => {
    const el = audioPlayer.value;
    if (isPlaying.value || pendingPlay) {
      el.pause();
      return;
    }
    playbackError.value = false;
    pendingPlay = true;
    emitter.emit('pause_playing_audio', uid);
    try {
      await el.play();
    } catch (error) {
      if (!disposed && error.name !== 'AbortError') onError();
    } finally {
      pendingPlay = false;
    }
  };
  const toggleMute = () => {
    audioPlayer.value.muted = !audioPlayer.value.muted;
    isMuted.value = audioPlayer.value.muted;
  };
  const onTimeUpdate = event => {
    if (!disposed) currentTime.value = event.currentTarget.currentTime;
  };
  const seek = event => {
    const time = Number(event.target.value);
    audioPlayer.value.currentTime = time;
    currentTime.value = time;
  };
  const onEnd = event => {
    if (disposed) return;
    isPlaying.value = false;
    currentTime.value = 0;
    playbackSpeed.value = 1;
    event.currentTarget.playbackRate = 1;
  };
  const changePlaybackSpeed = () => {
    const speeds = [1, 1.5, 2];
    playbackSpeed.value =
      speeds[(speeds.indexOf(playbackSpeed.value) + 1) % speeds.length];
    audioPlayer.value.playbackRate = playbackSpeed.value;
  };
  const downloadAudio = async () => {
    const { fileType, dataUrl, extension } = attachment();
    try {
      await downloadFile({ url: dataUrl, type: fileType, extension });
    } catch {
      onError();
    }
  };
  const formatTime = time => {
    if (!Number.isFinite(time) || time < 0) return '00:00';
    const minutes = Math.floor(time / 60)
      .toString()
      .padStart(2, '0');
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  return {
    isPlaying,
    isMuted,
    currentTime,
    duration,
    playbackSpeedLabel,
    playbackError,
    onLoadedMetadata,
    onTimeUpdate,
    onPlay,
    onPause,
    onError,
    onEnd,
    playOrPause,
    toggleMute,
    seek,
    changePlaybackSpeed,
    downloadAudio,
    formatTime,
  };
};
